import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { RENTALS, BRAND, IMAGES } from "@/lib/constants";
import { ArrowUpRight, Check, Phone, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Rentals() {
  const [selected, setSelected] = useState(RENTALS[0].id);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    start_date: "",
    end_date: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const vehicle = RENTALS.find((r) => r.id === selected);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.start_date) {
      return toast.error("Name, email, phone and start date are required");
    }
    setSubmitting(true);
    try {
      const days = form.end_date && form.start_date
        ? Math.max(1, Math.ceil((new Date(form.end_date) - new Date(form.start_date)) / 86400000))
        : 1;
      const message = `RENTAL INQUIRY — ${vehicle.name} (${vehicle.year})
Dates: ${form.start_date}${form.end_date ? " to " + form.end_date : ""} (${days} day${days > 1 ? "s" : ""})
Estimated starting at: $${vehicle.price_day * days}
Notes: ${form.notes || "—"}`;
      await axios.post(`${API}/contact`, {
        name: form.full_name,
        email: form.email,
        phone: form.phone,
        message,
      });
      setDone(true);
      toast.success("Inquiry received. We'll text you within an hour.");
    } catch (err) {
      console.error(err);
      toast.error("Could not send inquiry. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header />

      <section className="relative pt-32 pb-20" data-testid="rentals-page">
        <div className="absolute top-0 inset-x-0 h-[420px] -z-10 opacity-25">
          <img src={IMAGES.interior} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-void/60 via-void/85 to-void" />
        </div>

        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-12 mb-20">
            <div className="lg:col-span-7">
              <div className="overline mb-6">Self-Drive · Daily Rentals</div>
              <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl text-ivory leading-[0.95] tracking-tight" data-testid="rentals-title">
                Take the wheel.
                <br />
                <span className="italic text-gold">Keep the keys.</span>
              </h1>
            </div>
            <div className="lg:col-span-5 flex items-end">
              <p className="text-ivory-muted text-lg leading-relaxed">
                Want to drive yourself? Two distinct vehicles available for daily, weekend, or weekly rental in the Pittsburgh area. Insurance verification required. Final fare adjusts with mileage and rental duration.
              </p>
            </div>
          </div>

          {/* Vehicle Tabs */}
          <div className="flex flex-wrap gap-3 mb-10" data-testid="rental-tabs">
            {RENTALS.map((r) => (
              <button
                key={r.id}
                onClick={() => setSelected(r.id)}
                className={`px-6 py-3 border text-sm tracking-widest uppercase transition ${selected === r.id ? "bg-gold text-void border-gold" : "border-gold-soft text-ivory-muted hover:text-gold hover:border-gold"}`}
                data-testid={`rental-tab-${r.id}`}
              >
                {r.year} {r.name}
              </button>
            ))}
          </div>

          {/* Vehicle Showcase */}
          <div className="grid lg:grid-cols-12 gap-12 items-start mb-24" data-testid={`rental-showcase-${vehicle.id}`}>
            <div className="lg:col-span-7 editorial-hover">
              <div className="aspect-[5/4] overflow-hidden bg-emerald-deep">
                <img src={vehicle.image} alt={`${vehicle.name} ${vehicle.year}`} className="w-full h-full object-cover" />
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="overline mb-3">{vehicle.year}</div>
              <h2 className="font-serif-display text-4xl lg:text-5xl text-ivory leading-tight mb-4">{vehicle.name}</h2>
              <p className="text-ivory-muted text-lg leading-relaxed mb-8">{vehicle.tagline}</p>

              <div className="flex items-baseline gap-3 mb-2">
                <div className="overline">Starting at</div>
                <div className="font-serif-display text-5xl text-gold">${vehicle.price_day}</div>
                <div className="text-ivory-muted text-sm">/ day</div>
              </div>
              <div className="text-[11px] text-ivory-muted tracking-widest uppercase mb-10">Final fare adjusts with mileage</div>

              <div className="grid grid-cols-2 gap-4 mb-10">
                {vehicle.specs.map((s) => (
                  <div key={s.label} className="border-l border-gold-soft pl-4">
                    <div className="overline mb-1" style={{ fontSize: "0.6rem" }}>{s.label}</div>
                    <div className="text-ivory text-sm">{s.value}</div>
                  </div>
                ))}
              </div>

              <div className="border border-gold-soft p-6 bg-emerald-deep mb-10">
                <div className="overline mb-4">Features</div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-ivory-muted">
                  {vehicle.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-gold mt-0.5 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <a href="#rental-inquiry" className="btn-gold px-8 py-4 inline-flex items-center gap-3" data-testid="rental-inquire-cta">
                Reserve This Vehicle <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Inquiry Form */}
          <div id="rental-inquiry" className="grid lg:grid-cols-12 gap-12 pt-12 border-t border-gold-soft">
            <div className="lg:col-span-5">
              <div className="overline mb-4">Reserve · {vehicle.name}</div>
              <h3 className="font-serif-display text-4xl text-ivory leading-tight mb-6">Tell us your dates.</h3>
              <p className="text-ivory-muted text-base leading-relaxed mb-8">
                We'll confirm availability, share the rental agreement, and lock in your dates within the hour. Insurance + valid driver's license required at pickup.
              </p>
              <div className="space-y-4 text-sm">
                <a href={`tel:${BRAND.phone}`} className="flex items-center gap-3 text-ivory hover:text-gold" data-testid="rental-call">
                  <Phone className="h-4 w-4 text-gold" /><span className="tracking-widest">{BRAND.phoneVanity}</span>
                </a>
                <a href={`mailto:${BRAND.email}`} className="flex items-center gap-3 text-ivory hover:text-gold" data-testid="rental-email">
                  <Mail className="h-4 w-4 text-gold" /><span>{BRAND.email}</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-7">
              {done ? (
                <div className="border border-gold-soft p-10 bg-emerald-deep text-center" data-testid="rental-success">
                  <Check className="h-10 w-10 text-gold mx-auto mb-4" />
                  <h4 className="font-serif-display text-3xl text-ivory mb-3">Inquiry received.</h4>
                  <p className="text-ivory-muted text-sm">We've got your request for the {vehicle.year} {vehicle.name}. Expect a text from us within the hour.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="border border-gold-soft p-8 bg-emerald-deep space-y-5" data-testid="rental-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-[0.25em] text-ivory-muted mb-2">Full Name</label>
                      <input className="luxe-input w-full" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} placeholder="Jordan Williams" data-testid="rental-name" />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-[0.25em] text-ivory-muted mb-2">Phone</label>
                      <input className="luxe-input w-full" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="(412) 555-0123" data-testid="rental-phone" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs uppercase tracking-[0.25em] text-ivory-muted mb-2">Email</label>
                      <input type="email" className="luxe-input w-full" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@example.com" data-testid="rental-email-input" />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-[0.25em] text-ivory-muted mb-2">Start Date</label>
                      <input type="date" className="luxe-input w-full" value={form.start_date} onChange={(e) => update("start_date", e.target.value)} data-testid="rental-start" />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-[0.25em] text-ivory-muted mb-2">End Date</label>
                      <input type="date" className="luxe-input w-full" value={form.end_date} onChange={(e) => update("end_date", e.target.value)} data-testid="rental-end" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs uppercase tracking-[0.25em] text-ivory-muted mb-2">Notes</label>
                      <textarea rows={3} className="luxe-textarea w-full" value={form.notes} onChange={(e) => update("notes", e.target.value)} placeholder="Pickup time, anticipated mileage, any questions…" data-testid="rental-notes" />
                    </div>
                  </div>
                  <button type="submit" disabled={submitting} className="btn-gold px-8 py-4 inline-flex items-center gap-3 disabled:opacity-50" data-testid="rental-submit">
                    {submitting ? "Sending…" : "Send Inquiry"} <ArrowUpRight className="h-4 w-4" />
                  </button>
                  <p className="text-[11px] text-ivory-muted tracking-widest uppercase">No charge today · We confirm by text within the hour</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
